import type * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import type {
  AnchorHTMLAttributes,
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes,
} from "react";

import type { TIconButtonProps } from "@/ui/components/primitives";
import type { TIconKey } from "@/ui/components/primitives/Icon";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const HEADER_MENU_DESKTOP_MEDIA_QUERY = "(min-width: 768px)";

export const HEADER_MENU_LINKS_VARIANTS = [
  "navigation-menu",
  "sheet",
] as const;

/*-------------------------------------
|  Types
|-------------------------------------*/

export type THeaderMenuToggleElement = ComponentRef<"span">;

export type THeaderMenuToggleProps = ComponentPropsWithoutRef<"span"> & {
  open: boolean;
};

export type THeaderMenuToggleIconElement = ComponentRef<"svg">;

export type THeaderMenuToggleIconProps = Omit<
  ComponentPropsWithoutRef<"svg">,
  "children"
> & {
  open: boolean;
};

export type THeaderMenuTriggerElement = ComponentRef<
  typeof NavigationMenuPrimitive.Trigger
>;

export type THeaderMenuTriggerProps = ComponentPropsWithoutRef<
  typeof NavigationMenuPrimitive.Trigger
>;

export type THeaderMenuViewportElement = ComponentRef<
  typeof NavigationMenuPrimitive.Viewport
>;

export type THeaderMenuViewportProps = ComponentPropsWithoutRef<
  typeof NavigationMenuPrimitive.Viewport
>;

export type THeaderMenuLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  icon?: TIconKey;
};

export type THeaderMenuLinkItem = {
  label: string;
  href: string;
  icon?: TIconKey;
  className?: string;
  linkProps?: Omit<
    THeaderMenuLinkProps,
    "children" | "className" | "href" | "icon"
  >;
};

export type THeaderMenuSeparatorProps = HTMLAttributes<HTMLLIElement> & {
  orientation?: "horizontal" | "vertical";
};

export type THeaderMenuSeparatorItem = {
  type: "separator";
  id: string;
  className?: string;
  orientation?: THeaderMenuSeparatorProps["orientation"];
  itemProps?: Omit<
    THeaderMenuSeparatorProps,
    "children" | "className" | "orientation"
  >;
};

export type THeaderMenuItem = THeaderMenuLinkItem | THeaderMenuSeparatorItem;

export type THeaderMenuLinksVariant =
  (typeof HEADER_MENU_LINKS_VARIANTS)[number];

export type THeaderMenuLinksProps = {
  className?: string;
  items: readonly THeaderMenuItem[];
  onNavigate?: () => void;
  variant: THeaderMenuLinksVariant;
};

export type THeaderMenuVariantProps = {
  items: readonly THeaderMenuItem[];
};

export type THeaderMenuProps = {
  items?: readonly THeaderMenuItem[];
};

export type THeaderProps = THeaderMenuProps & {
  menu?: THeaderMenuProps;
  actions?: {
    primary: TIconButtonProps;
  };
};
